/** @prettier */

import { useEffect, useState } from 'react';
import canUseDOM from '@surfline/web-common/dist/canUseDOM';

const LOCAL_STORAGE_KEY = 'page_views';

/**
 * React Hook for storing page views in local storage. Given a page name,
 * this hook will store the amount of times the page has been mounted by
 * the user in local storage. Everytime the component is mounted the `useEffect`
 * hook will be called. An optional parameter `updateTrigger` can be passed.
 * this update trigger should be a prop that when changed should trigger a page
 * view counter increment.
 *
 * @export
 * @param {String} pageName
 * @param {Any} updateTrigger optional parameter to trigger updates when the parameter changes
 * @returns
 */
export default function usePageViews(pageName, updateTrigger = null) {
  // Pull the `localStorage.page_views object and set it in state
  const [pageViewsObject] = useState(() => {
    if (!canUseDOM) return {};
    const currValue = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
    return currValue;
  });

  // Pull the page view count for the given page and set it in state
  const [pageViews, setPageViews] = useState(() =>
    pageViewsObject[pageName] ? pageViewsObject[pageName].viewCount : 0
  );

  // On mount we want to update the view count
  useEffect(() => {
    if (canUseDOM) {
      const currUnixTimeStamp = Math.floor(+new Date() / 1000);
      const nextViewCount = pageViews + 1;

      const nextValue = JSON.stringify({
        ...pageViewsObject,
        [pageName]: {
          viewCount: nextViewCount,
          lastVisitedAt: currUnixTimeStamp,
        },
      });

      setPageViews(nextViewCount);
      localStorage.setItem('page_views', nextValue);
    }
    // Disabling the following line because we only want this hook to increment
    // when the update trigger happens or on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTrigger]);

  return pageViews;
}
